import React, { useState, useEffect, useCallback, useRef } from 'react';
import './LabelSelectionModal.scss';
import { getLabelsByCourtref, getLabelsByUser, updateLabel } from '../../services/api/LabelsApi';
import { getCourtTypeByCourtRef } from '../../services/api/MyClientsApi';
import { useToast } from '../../context/ToastContext';
import { getLabelByCourtRef } from '../../services/api/LabelApi';

const LabelSelectionModal = ({
  isOpen,
  onClose,
  courtRef,
  onLabelAdded,
  onOpenLabelForm,
}) => {
  const [selectionType, setSelectionType] = useState('new');
  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // New state for remove labels functionality - CHANGED TO ARRAY
  const [assignedLabels, setAssignedLabels] = useState([]);
  const [labelsToRemove, setLabelsToRemove] = useState([]); // Changed from labelToRemove to labelsToRemove array
  const [isRemovingLabel, setIsRemovingLabel] = useState(false);
  
  const { showToast } = useToast();
  const modalRef = useRef(null);

  const fetchLabels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLabelsByUser();

      const responseData = response?.data || {};
      const labelGroups = responseData.labels || [];

      const allLabels = [];
      labelGroups.forEach(group => {
        if (group.labels && Array.isArray(group.labels)) {
          allLabels.push(...group.labels);
        }
      });

      setAvailableLabels(allLabels);
    } catch (error) {
      console.error('Error fetching labels:', error);
      setAvailableLabels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignedLabels = useCallback(async () => {
    if (!courtRef) {
      console.log('No courtRef provided');
      return;
    }
    
    try {
      setLoading(true);
      const response = await getLabelByCourtRef(courtRef);
      
      const labels = Array.isArray(response?.data) ? response.data : [];
      setAssignedLabels(labels);
      
    } catch (error) {
      console.error('Error fetching assigned labels:', error);
      console.error('Error details:', error.response?.data || error.message);
      setAssignedLabels([]);
      if (error.response?.status !== 404) {
        showToast('Failed to fetch assigned labels', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [courtRef, showToast]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedLabels([]);
      setSearchTerm('');
      setIsDropdownOpen(false);
      setSelectionType('new');
      setLabelsToRemove([]); // Reset to empty array
      setAssignedLabels([]);
    }
  }, [isOpen]);

  // Fetch data based on selection type
  useEffect(() => {
    if (!isOpen) return;
    
    
    if (selectionType === 'existing') {
      fetchLabels();
    } else if (selectionType === 'remove') {
      fetchAssignedLabels();
    }
  }, [isOpen, selectionType, fetchLabels, fetchAssignedLabels]);

  const filteredLabels = Array.isArray(availableLabels)
    ? availableLabels.filter(label => {
      if (!label || !label.name) return false;

      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        return label.name.toLowerCase().includes(searchLower) ||
          (label.description && label.description.toLowerCase().includes(searchLower));
      }

      return true;
    })
    : [];

  const handleLabelSelect = (labelId) => {
    if (labelId && !selectedLabels.includes(labelId)) {
      setSelectedLabels(prev => [...prev, labelId]);
    }
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setTimeout(() => setIsDropdownOpen(false), 150);
    }
  };

  const handleRemoveSelectedLabel = (labelIdToRemove) => {
    setSelectedLabels(prev => prev.filter(id => id !== labelIdToRemove));
  };

  // MODIFIED: Handle label click in remove mode - now supports multiple selection
  const handleAssignedLabelClick = (labelId) => {
    setLabelsToRemove(prev => {
      if (prev.includes(labelId)) {
        // Remove from selection if already selected
        return prev.filter(id => id !== labelId);
      } else {
        // Add to selection
        return [...prev, labelId];
      }
    });
  };

  // MODIFIED: Handle multiple label removal
  const handleRemoveLabels = async () => {
    if (!labelsToRemove || labelsToRemove.length === 0 || !courtRef) {
      showToast('Please select at least one label to remove', 'warning');
      return;
    }

    setIsRemovingLabel(true);
    
    try {
      
      // Determine court type once
      const courtType = await getCourtTypeFromRef(courtRef);
      const courtCasesField = `${courtType}CourtCases`;
      
      let successCount = 0;
      let failedLabels = [];
      
      // Process each label removal
      for (const labelId of labelsToRemove) {
        try {
          // Find the label data from assignedLabels
          const labelData = assignedLabels.find(label => label._id === labelId);
          
          if (!labelData) {
            console.error(`Label data not found for ID: ${labelId}`);
            failedLabels.push(labelId);
            continue;
          }
          
          // Remove the courtRef from the appropriate array
          const updatedCourtCases = (labelData[courtCasesField] || []).filter(
            ref => ref !== courtRef
          );

          // Prepare the label data with the courtRef removed
          const updatedLabelData = {
            highCourtCases: courtType === 'high' 
              ? updatedCourtCases 
              : (labelData.highCourtCases || []),
            districtCourtCases: courtType === 'district' 
              ? updatedCourtCases 
              : (labelData.districtCourtCases || []),
            otherCourtCases: courtType === 'other' 
              ? updatedCourtCases 
              : (labelData.otherCourtCases || [])
          };

          const updateResponse = await updateLabel(labelId, updatedLabelData);
          
          successCount++;
          
        } catch (error) {
          console.error(`Error removing label ${labelId}:`, error);
          failedLabels.push(labelId);
        }
      }
      
      // Show appropriate success/error messages
      if (successCount > 0) {
        const successMessage = successCount === 1 
          ? `1 label removed successfully` 
          : `${successCount} labels removed successfully`;
        showToast(successMessage, 'success');
        
        // Refresh the assigned labels to reflect the changes
        await fetchAssignedLabels();
        
        // Call the callback to refresh case details
        if (onLabelAdded && typeof onLabelAdded === 'function') {
          await onLabelAdded();
        }
      }
      
      if (failedLabels.length > 0) {
        const failMessage = failedLabels.length === 1
          ? `Failed to remove 1 label`
          : `Failed to remove ${failedLabels.length} labels`;
        showToast(failMessage, 'error');
      }
      
      // Reset state
      setLabelsToRemove([]);
      
      // Close modal after successful removal
      if (successCount > 0 && failedLabels.length === 0) {
        onClose();
      }
      
    } catch (error) {
      console.error('Error removing labels:', error);
      showToast('Failed to remove labels. Please try again.', 'error');
    } finally {
      setIsRemovingLabel(false);
    }
  };

  const handleAddNewLabel = () => {
    onClose();
    onOpenLabelForm();
  };

  const handleCloseModal = () => {
    setSelectedLabels([]);
    setSearchTerm('');
    setIsDropdownOpen(false);
    setSelectionType('new');
    setLabelsToRemove([]); // Reset to empty array
    onClose();
  };

  const handleAddSelectedLabels = async () => {
    if (!Array.isArray(selectedLabels) || selectedLabels.length === 0) return;

    setLoading(true);
    let skippedLabels = [];
    let processedLabels = 0;

    try {
      for (const labelId of selectedLabels) {
        if (!labelId) continue;

        const currentLabel = availableLabels.find(label => label._id === labelId);

        if (!currentLabel) {
          console.error('Could not find label in availableLabels:', labelId);
          continue;
        }

        try {
          const courtTypeResponse = await getCourtTypeByCourtRef(courtRef);

          if (courtTypeResponse && courtTypeResponse.data) {
            const courtType = await getCourtTypeFromRef(courtRef);
            const courtCasesField = `${courtType}CourtCases`;
            const existingCourtCases = currentLabel[courtCasesField] || [];

            if (existingCourtCases.includes(courtRef)) {
              skippedLabels.push(currentLabel.name);
              continue;
            }
          }
        } catch (error) {
          console.log('Court reference not found in API, proceeding with assignment...');
        }

        const courtType = await getCourtTypeFromRef(courtRef);

        const courtCasesField = `${courtType}CourtCases`;
        const existingCourtCases = currentLabel[courtCasesField] || [];


        if (existingCourtCases.includes(courtRef)) {
          skippedLabels.push(currentLabel.name);
          continue;
        }

        const updatedCourtCases = [...existingCourtCases, courtRef];

        const labelData = {
          highCourtCases: courtType === 'high' ? updatedCourtCases : (currentLabel.highCourtCases || []),
          districtCourtCases: courtType === 'district' ? updatedCourtCases : (currentLabel.districtCourtCases || []),
          otherCourtCases: courtType === 'other' ? updatedCourtCases : (currentLabel.otherCourtCases || [])
        };

        const apiResponse = await updateLabel(labelId, labelData);


        processedLabels++;
      }

      if (skippedLabels.length > 0) {
        const skippedMessage = skippedLabels.length === 1
          ? `Label "${skippedLabels[0]}" is already assigned to this court.`
          : `${skippedLabels.length} labels (${skippedLabels.join(', ')}) are already assigned to this court.`;
        showToast(skippedMessage, 'warning');
      }

      if (processedLabels > 0) {
        const successMessage = processedLabels === 1
          ? `1 label has been successfully added to this court.`
          : `${processedLabels} labels have been successfully added to this court.`;
        showToast(successMessage, 'success');
      }

      if (processedLabels === 0 && skippedLabels.length === 0) {
        showToast('No labels were processed.', 'info');
      }

      if (processedLabels > 0 && onLabelAdded && typeof onLabelAdded === 'function') {
        await onLabelAdded(selectedLabels.filter((labelId) => {
          const label = availableLabels.find(l => l._id === labelId);
          return label && !skippedLabels.includes(label.name);
        }));
      }

      setSelectedLabels([]);
      setSearchTerm('');
      setIsDropdownOpen(false);

      onClose();

    } catch (error) {
      console.error('Error adding labels:', error);
      showToast('Error occurred while adding labels. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCourtTypeFromRef = async (courtRef) => {
    if (!courtRef) {
      return 'other';
    }


    try {
      const response = await getCourtTypeByCourtRef(courtRef);

      const courtType = response?.data?.source || response?.data?.courtType || response?.data?.type || 'other';

      const normalizedType = courtType.toLowerCase();
      if (normalizedType.includes('high')) {
        return 'high';
      } else if (normalizedType.includes('district') || normalizedType.includes('dist')) {
        return 'district';
      } else {
        return 'other';
      }

    } catch (error) {
      console.error('Error calling court type API:', error);
      return 'other';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="label-selection-modal-backdrop">
      <div ref={modalRef} className="label-selection-modal">
        <h2 className="label-selection-modal-title">Add/Update Labels</h2>

        <div className="label-selection-options">
          <label className="label-selection-option">
            <input
              type="radio"
              value="new"
              checked={selectionType === 'new'}
              onChange={(e) => {
                setSelectionType(e.target.value);
                setLabelsToRemove([]); // Reset array
              }}
            />
            <span>Add New Label</span>
          </label>
          <p className="label-selection-desc">
            Create a completely new label with custom properties.
          </p>

          <label className="label-selection-option">
            <input
              type="radio"
              value="existing"
              checked={selectionType === 'existing'}
              onChange={(e) => {
                setSelectionType(e.target.value);
                setLabelsToRemove([]); // Reset array
              }}
            />
            <span>Assign Existing Label</span>
          </label>
          <p className="label-selection-desc">
            Select from your existing labels to assign to this court.
          </p>

          <label className="label-selection-option">
            <input
              type="radio"
              value="remove"
              checked={selectionType === 'remove'}
              onChange={(e) => {
                const newValue = e.target.value;
                setSelectionType(newValue);
                setSelectedLabels([]);
                setLabelsToRemove([]); // Reset array
              }}
            />
            <span>Remove Label</span>
          </label>
          <p className="label-selection-desc">
            Remove assigned labels from this court case.
          </p>
        </div>

        {selectionType === 'existing' && (
          <div className="existing-labels-section">
            <div className="searchable-dropdown-container" onBlur={handleDropdownBlur}>
              <label htmlFor="label-search" className="dropdown-label">
                Search and select labels to add:
              </label>

              <div className="searchable-dropdown">
                <div className="search-input-container">
                  <input
                    id="label-search"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder={loading ? "Loading labels..." : "Type to search labels..."}
                    className="search-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="dropdown-toggle-btn"
                    disabled={loading}
                  >
                    ▼
                  </button>
                </div>

                {isDropdownOpen && (
                  <div className="dropdown-options">
                    {loading ? (
                      <div className="dropdown-option loading-option">Loading labels...</div>
                    ) : filteredLabels.length === 0 ? (
                      <div className="dropdown-option no-results-option">
                        {searchTerm ? `No labels found for "${searchTerm}"` : "No labels available"}
                      </div>
                    ) : (
                      filteredLabels.map(label => (
                        <div
                          key={label._id}
                          onClick={() => handleLabelSelect(label._id)}
                          className={`dropdown-option ${selectedLabels.includes(label._id) ? 'selected' : ''}`}
                        >
                          <div className="option-content">
                            <span className="option-name">{label.name}</span>
                            {label.description && (
                              <span className="option-description">{label.description}</span>
                            )}
                          </div>
                          {selectedLabels.includes(label._id) && (
                            <span className="selected-indicator">✓</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {Array.isArray(selectedLabels) && selectedLabels.length > 0 && (
              <div className="selected-labels-container">
                <h4 className="selected-labels-title">Selected Labels:</h4>
                <div className="selected-labels-list">
                  {selectedLabels.map(labelId => {
                    const label = availableLabels.find(l => l._id === labelId);
                    return label ? (
                      <div key={labelId} className="selected-label-item">
                        <div className="label-info">
                          <span className="label-name">{label.name}</span>
                          {label.description && (
                            <span className="label-description">{label.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedLabel(labelId)}
                          className="remove-label-btn"
                          title="Remove label"
                        >
                          ×
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {Array.isArray(selectedLabels) && selectedLabels.length > 0 && (
              <div className="selected-count">
                {selectedLabels.length} label{selectedLabels.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        )}

        {selectionType === 'remove' && (
          <div className="remove-labels-section">
            <h4 className="assigned-labels-title">Assigned Labels (Click to select for removal):</h4>
            
            
            {loading ? (
              <div className="loading-message">Loading assigned labels...</div>
            ) : assignedLabels.length === 0 ? (
              <div className="no-labels-message">
                No labels assigned to this court case.
              </div>
            ) : (
              <>
                <div className="assigned-labels-grid">
                  {assignedLabels.map(label => {
                    const isSelected = labelsToRemove.includes(label._id);
                    return (
                      <button
                        key={label._id}
                        onClick={() => handleAssignedLabelClick(label._id)}
                        className={`assigned-label-pill ${isSelected ? 'selected-for-removal' : ''}`}
                        style={{ 
                          backgroundColor: isSelected ? '#f3f4f6' : label.color,
                          color: isSelected ? '#6b7280' : '#ffffff',
                          opacity: isSelected ? 0.6 : 1
                        }}
                      >
                        <span className="label-pill-name">{label.name}</span>
                        {isSelected && (
                          <span className="remove-indicator">×</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            
            {labelsToRemove.length > 0 && (
              <div className="removal-hint">
                {labelsToRemove.length} label{labelsToRemove.length !== 1 ? 's' : ''} selected for removal. Click "Remove Labels" button below to confirm.
              </div>
            )}
          </div>
        )}

        <div className="label-selection-actions">
          <button className="btn-cancel" onClick={handleCloseModal}>
            Cancel
          </button>

          {selectionType === 'new' ? (
            <button className="btn-primary" onClick={handleAddNewLabel}>
              Create New Label
            </button>
          ) : selectionType === 'existing' ? (
            <button
              className="btn-primary"
              onClick={handleAddSelectedLabels}
              disabled={!Array.isArray(selectedLabels) || selectedLabels.length === 0 || loading}
            >
              {loading ? 'Adding...' : `Add Selected Labels (${Array.isArray(selectedLabels) ? selectedLabels.length : 0})`}
            </button>
          ) : (
            <button
              className="btn-danger"
              onClick={handleRemoveLabels}
              disabled={!labelsToRemove || labelsToRemove.length === 0 || isRemovingLabel}
            >
              {isRemovingLabel ? 'Removing...' : `Remove Labels (${labelsToRemove.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelSelectionModal;